import React, { useState } from 'react'
import { Heading, Text, Button } from '@primer/react-brand'

import { API_BASE_URL } from '../../config/apiConfig'

// Email-not-confirmed gate. An admin reached this by clicking Subscribe while
// the organization's email is still unconfirmed; the server has already sent a
// confirmation link to the primary contact (MainController#confirm_email).
//
// Design: EmailGateScreen in Prototype C_A_primer_v2.html — a two-column page
// (sticky primary-contact card on the left, steps + guidance on the right).
// Colors map to @primer/react-brand base color scale tokens (see the deviation
// notes in the spec for the minor approximations).
//
// Renders content-only into the header-only app chrome (civic_lens_subscribe
// layout); no page Header/Footer of its own.
//
// Props: { orgName, primaryContactName, primaryContactEmail }
const ConfirmEmailGatePage = ({
  orgName,
  primaryContactName,
  primaryContactEmail,
}) => {
  const [resendState, setResendState] = useState('idle') // idle | sending | sent

  const handleResend = async () => {
    if (resendState !== 'idle') return
    setResendState('sending')
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v2/organizations/resend_email_confirmation`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': csrf,
          },
        }
      )
      setResendState(response.ok ? 'sent' : 'idle')
    } catch (err) {
      setResendState('idle')
    }
  }

  const steps = [
    <>We&rsquo;ve just re-emailed a confirmation link to your organization&rsquo;s primary contact, <strong>{primaryContactName}</strong> at <strong>{primaryContactEmail}</strong>.</>,
    <>They need to open the email and confirm their address.</>,
    <>You or they can then come back and subscribe.</>,
  ]

  return (
    <div
      style={{
        maxWidth: 960,
        width: '100%',
        margin: '0 auto',
        padding: '48px 40px 64px',
      }}
    >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: 56,
            alignItems: 'flex-start',
          }}
        >
          {/* Left: primary-contact card */}
          <div
            style={{
              background: 'var(--base-color-scale-gray-0)',
              border: '1px solid var(--base-color-scale-gray-1)',
              borderRadius: 6,
              padding: 24,
              position: 'sticky',
              top: 24,
            }}
          >
            <Text
              as="p"
              style={{
                margin: '0 0 8px',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--base-color-scale-gray-6)',
              }}
            >
              Primary contact
            </Text>
            <Text
              as="p"
              size="300"
              weight="semibold"
              style={{
                margin: 0,
                color: 'var(--base-color-scale-gray-9)',
                letterSpacing: '-0.01em',
              }}
            >
              {primaryContactName}
            </Text>
            <Text
              as="p"
              size="100"
              style={{
                margin: '3px 0 0',
                color: 'var(--base-color-scale-gray-6)',
                overflowWrap: 'anywhere',
              }}
            >
              {primaryContactEmail}
            </Text>
            <hr
              style={{
                border: 'none',
                borderTop: '1px solid var(--base-color-scale-gray-1)',
                margin: '18px 0',
              }}
            />
            <Text
              as="p"
              style={{
                margin: 0,
                fontSize: 12,
                color: 'var(--base-color-scale-gray-4)',
                lineHeight: 1.5,
              }}
            >
              This is the primary contact for your organization, {orgName}.{' '}
              {primaryContactName} is the only one who will be able to confirm
              the email for your organization. If you are unable to reach this
              person, please contact us.
            </Text>
          </div>

          {/* Right: instructions */}
          <div>
            <Heading as="h1" size="5">Verify your organization&rsquo;s email</Heading>
            <Text
              as="p"
              size="300"
              variant="muted"
              style={{ marginTop: 6, marginBottom: 22, lineHeight: 1.65 }}
            >
              Your organization&rsquo;s email address hasn&rsquo;t been confirmed
              yet, subscribing isn&rsquo;t available until it has been confirmed.
              Here&rsquo;s what to do next:
            </Text>

            <ol
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {steps.map((step, index) => (
                <li
                  key={index}
                  style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'var(--base-color-scale-gray-9)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {index + 1}
                  </span>
                  <Text
                    as="span"
                    size="100"
                    style={{
                      color: 'var(--base-color-scale-gray-7)',
                      lineHeight: 1.5,
                      paddingTop: 2,
                    }}
                  >
                    {step}
                  </Text>
                </li>
              ))}
            </ol>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 24 }}>
              <Button
                as="a"
                href="/monitoring"
                variant="primary"
                style={{
                  backgroundColor: 'var(--base-color-scale-blue-5)',
                  borderColor: 'var(--base-color-scale-blue-5)',
                  borderRadius: '6px',
                }}
              >
                Back to dashboard
              </Button>
              <Button
                variant="subtle"
                onClick={handleResend}
                disabled={resendState !== 'idle'}
              >
                {resendState === 'sent'
                  ? 'Sent again — check the inbox'
                  : 'Resend confirmation email'}
              </Button>
            </div>
          </div>
        </div>
      </div>
  )
}

export default ConfirmEmailGatePage
